import React, {useEffect, useState} from 'react';
import {
    OcrBatchBox,
    OcrBatchCard,
    OcrBatchContentArea,
    OcrBatchFileInfo,
    OcrBatchFileTypeBadge,
    OcrBatchHeader,
    OcrBatchLeftPanel,
    OcrBatchMainContainer,
    OcrBatchProcessingSpinner,
    OcrBatchRightPanel,
    OcrBatchStatusLabel
} from "./OcrBatch.styled";
import {useTranslation} from "react-i18next";
import ScanPanel from "./components/ScanPanel";
import ResultPanel from "./components/ResultPanel";
import {useNavigate, useParams} from "react-router-dom";
import {CircularProgress, Divider, IconButton, Tooltip} from "@mui/material";
import ExportDropdown from "../../components/ocr-batch/ExportDropdown";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {formatOcrFileSize} from "../../utils/ocr-utils";
import {OcrBatchFileResponse, OcrFileExportFormat, OcrFileStatus, OcrProcessedFile} from "../../models/ocr.models";
import {ocrService} from "../../services/ocr.service";
import {useOcrBatch} from "../../contexts/OcrBatchContext";

const OcrResultPreview = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const { batchId, fileId } = useParams<{ batchId: string; fileId: string }>();

    const {exportFiles} = useOcrBatch();

    const [file, setFile] = useState<OcrBatchFileResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const fetchFile = async () => {
            setLoading(true);
            try {
                const data = await ocrService.getOcrBatchFile(Number(batchId), Number(fileId));
                if (!cancelled) setFile(data);
            } catch {
                if (!cancelled) setError(true);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchFile();

        return () => { cancelled = true; };
    }, [batchId, fileId]);

    useEffect(() => {
        if (!loading && (error || !file)) {
            navigate('/ocr/batch');
        }
    }, [loading, error, file, navigate]);

    if (loading) return <CircularProgress />;
    if (error || !file) return null;

    const handleBack = () => {
        navigate(`/ocr/batch/${batchId}`);
    };

    const statusMap: Record<OcrFileStatus, string> = {
        PENDING: t('ocr.status.pending'),
        PROCESSING: t('ocr.status.processing'),
        SUCCESS: t('ocr.status.success'),
        ERROR: t('ocr.status.error'),
    };

    const { fileName, mode, status, contentType, baseContent, size, pages } = file;

    return (
        <OcrBatchCard>
            <OcrBatchMainContainer $row>
                <OcrBatchLeftPanel as="section" aria-label={t('ocr.aria.previewOriginalFile')}>
                    <OcrBatchHeader>
                        <OcrBatchBox>
                            <Tooltip title={t('ocr.button.back')}>
                                <IconButton color="primary"
                                            aria-label={t('ocr.button.back') as string}
                                            onClick={handleBack}
                                >
                                    <ChevronLeftIcon sx={{ fontSize: 24 }} />
                                </IconButton>
                            </Tooltip>

                            <Divider orientation="vertical" flexItem />

                            <OcrBatchFileTypeBadge aria-hidden="true">
                                {contentType}
                            </OcrBatchFileTypeBadge>

                            <OcrBatchFileInfo>
                                <span className="name" aria-label={t('ocr.aria.fileName',{ name: fileName }) as string}>
                                    {fileName}
                                </span>
                                <span className="size" aria-label={t('ocr.aria.fileSize', {size: formatOcrFileSize(size)}) as string}>
                                    {formatOcrFileSize(size)}
                                </span>
                            </OcrBatchFileInfo>
                        </OcrBatchBox>

                        <OcrBatchStatusLabel
                            $status={status}
                            aria-live="polite"
                            aria-label={t('ocr.aria.fileStatus', {status: statusMap[status]}) as string}
                        >
                            {status === 'PROCESSING' && <OcrBatchProcessingSpinner aria-hidden="true" />}
                            {statusMap[status]}
                        </OcrBatchStatusLabel>
                    </OcrBatchHeader>

                    <OcrBatchContentArea>
                        <ScanPanel key={fileId} fileName={fileName} contentType={contentType} baseContent={baseContent}/>
                    </OcrBatchContentArea>
                </OcrBatchLeftPanel>

                <OcrBatchRightPanel as="section" aria-label={t('ocr.aria.ocrResult')}>
                    <OcrBatchHeader>
                        <OcrBatchBox style={{ marginLeft: 'auto' }}>
                            <ExportDropdown
                                onExport={exportFiles}
                                availableFormats={['PDF', 'TXT', 'DOCX'] as OcrFileExportFormat[]}
                                files={[file as OcrProcessedFile]}
                            />
                        </OcrBatchBox>
                    </OcrBatchHeader>

                    <OcrBatchContentArea>
                        <ResultPanel pages={pages} mode={mode ?? 'PLAIN_OCR'}/>
                    </OcrBatchContentArea>
                </OcrBatchRightPanel>
            </OcrBatchMainContainer>
        </OcrBatchCard>
    );
};

export default OcrResultPreview;